import type { Draft, PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';
import type { JsonFailure } from '@skynet/data-ws-core';
import { registerErrorMessage, isOkCommand, parseResponse } from '@skynet/data-ws-core';
import type {
  BalancesCorrectionTokenOption,
  BalancesState,
  JsonBalance,
  JsonBalancesSuccess,
} from './types';
import { BalanceCommands } from './types';
import type { SliceFactoryPropsWithMessages } from 'src/store/shared/types';
import { isFunction } from 'es-toolkit/predicate';

registerErrorMessage(BalanceCommands.CloseSmallTokens, {
  title: 'Failed to close small tokens',
  getDescription: (json: JsonFailure) => String(json.message),
});
registerErrorMessage(BalanceCommands.AddBalance, {
  title: 'Failed to save balances snapshot',
  getDescription: (json: JsonFailure) => String(json.message),
});

interface BalancesFactoryProps<T extends BalancesState> extends SliceFactoryPropsWithMessages<T> {
  extraCommands?: string[];
  onMessage?: (state: Draft<T>, json: Record<string, unknown>) => void;
}

export const createBalancesSlice = <T extends BalancesState>({
  prefix = 'shared',
  sliceName = 'balances',
  messages,
  initialState,
  extraReducers,
  extraCommands = [],
  onMessage,
}: BalancesFactoryProps<T>) => {
  if (!initialState) {
    throw new Error('Invalid initial state');
  }

  const allowedCommands = [...Object.values(BalanceCommands), ...extraCommands];

  return createSlice({
    name: `${prefix}/${sliceName}`,
    initialState,
    reducers: {
      setSelection(state, action: PayloadAction<JsonBalance['accountID'][]>) {
        state.selection = action.payload;
      },
      resetList(state) {
        state.selection = [];
        state.createBalanceSnapshotStatus = null;
        state.createWalletSnapshotStatus = null;
        state.addBalanceStatus = null;
        state.balancesCorrection = { open: false, data: null };
      },
      setCurrentProject(state, action: PayloadAction<number | null>) {
        if (state.currentProjectId !== action.payload) {
          state.currentProjectId = action.payload;
        }
      },
      openBalancesCorrection(
        state,
        action: PayloadAction<{
          accountID: string;
          tokenOpts: BalancesCorrectionTokenOption[];
        }>,
      ) {
        const { accountID, tokenOpts } = action.payload;
        state.balancesCorrection.open = true;
        state.balancesCorrection.data = { accountID, tokenOpts, allTokenOpts: tokenOpts };
      },
      closeBalancesCorrection(state) {
        state.balancesCorrection.open = false;
        state.balancesCorrection.data = null;
      },
      setBalancesCorrectionTokenOpts(
        state,
        action: PayloadAction<BalancesCorrectionTokenOption[]>,
      ) {
        if (state.balancesCorrection.data) {
          state.balancesCorrection.data.tokenOpts = action.payload;
        }
      },
    },
    extraReducers: (builder) => {
      // Handle WS messages
      builder.addCase(messages, (state, action) => {
        const { json, skipProcessing, error, errorCommand } = parseResponse(
          action.payload,
          allowedCommands,
        );

        if (skipProcessing || !json) return;

        if (error) {
          state.error = error;

          switch (errorCommand) {
            case BalanceCommands.AddBalance:
              if (state.createBalanceSnapshotStatus === 'pending') {
                state.createBalanceSnapshotStatus = 'error';
              }
              if (state.addBalanceStatus === 'pending') {
                state.addBalanceStatus = 'error';
              }
              break;
            case undefined:
            default:
              state.requestStatus = 'error';
          }

          return;
        }

        const { command, okCommand } = json;

        state.error = null;

        if (command === BalanceCommands.Balances) {
          const { balances, supportedTokens, id }: JsonBalancesSuccess = json;

          if (!id) return;

          if (balances) {
            state.list[id] = balances;
          }

          if (supportedTokens) {
            state.supportedTokens[id] = supportedTokens;
          }

          state.requestStatus = 'success';

          return;
        }

        if (isOkCommand(command) && okCommand === BalanceCommands.AddBalance) {
          if (state.createBalanceSnapshotStatus === 'pending') {
            state.createBalanceSnapshotStatus = 'success';
          }
          if (state.addBalanceStatus === 'pending') {
            state.addBalanceStatus = 'success';
          }
          return;
        }

        if (isFunction(onMessage)) {
          onMessage(state, json);
        }
      });

      // Domain-specific extra reducers
      extraReducers?.(builder);
    },
  });
};
